// @ts-nocheck baseline 2026-02-16
import { useState, useLayoutEffect, useMemo, useCallback } from "react";
import { ScrollView, Pressable } from "react-native";
import { PressableScale } from "pressto";
import { useRouter, useLocalSearchParams, useNavigation } from "expo-router";
import { View, Text, ScreenContainer, FormInput } from "@src/components";
import { Icon } from "@src/components/Icon";
import { useUnistyles } from "react-native-unistyles";
import { emailContactSchema } from "@src/features/contacts/schemas";
import { useEmailContactSubmit } from "@src/features/contacts/hooks";
import { ContactCategory } from "@src/features/contacts/types";
import { EmailSuggestion } from "@src/features/contacts/types";
import { useCameraContacts, useCameraEmailSuggestions } from "@src/services/api/hooks";
import { showErrorToast, showSuccessToast } from "@src/utils/toast";
import { FeatureGate } from "@src/services/feature-flags/FeatureGate";
import { FEATURE_FLAGS, useFeatureFlags } from "@src/services/feature-flags/feature-flags";
import { useHeaderBackButton } from "@src/hooks/useHeaderBackButton";
import { useTenantId } from "@src/features/tenants";

export default function AddEmailContact() {
  const router = useRouter();
  const navigation = useNavigation();
  const { theme } = useUnistyles();
  const tenantId = useTenantId();
  const { category, siteId, cameraKey } = useLocalSearchParams<{
    category: ContactCategory;
    siteId?: string;
    cameraKey?: string;
  }>();
  const fallbackRoute = useMemo(() => {
    if (!category || !siteId || !cameraKey || !tenantId) return undefined;
    return `/tenants/${encodeURIComponent(tenantId)}/sites/${encodeURIComponent(siteId)}/cameras/${encodeURIComponent(
      cameraKey,
    )}/activity-notification`;
  }, [category, siteId, cameraKey, tenantId]);
  const { headerLeft } = useHeaderBackButton(fallbackRoute);

  const { getBooleanFlag } = useFeatureFlags();
  const hasCameraContext = Boolean(siteId && cameraKey);
  const { data: cameraContacts } = useCameraContacts(siteId, cameraKey);

  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(true);

  const { data: apiSuggestions = [] } = useCameraEmailSuggestions(
    siteId,
    cameraKey,
    undefined,
    hasCameraContext && showSuggestions && getBooleanFlag(FEATURE_FLAGS.EMAIL_CONTACT_SUGGESTIONS),
  );

  const suggestions = apiSuggestions;

  const { submitEmail, isSubmitting } = useEmailContactSubmit({
    siteId,
    cameraKey,
    cameraContacts: cameraContacts ?? null,
  });

  const handleSubmitEmail = useCallback(
    (rawEmail: string) => {
      try {
        const validated = emailContactSchema.parse({
          email: rawEmail.trim(),
        });
        submitEmail(
          { email: validated.email },
          {
            onSuccess: () => {
              router.back();
              showSuccessToast({ message: "Contact added successfully" });
            },
            onError: (error: { message: string }) => {
              showErrorToast({ error });
            },
          },
        );
      } catch (err: unknown) {
        const issues = (err as { issues: { path: string[]; message: string }[] }).issues || [];
        const errorMessage = issues[0]?.message || "Invalid email address";
        setError(errorMessage);
        showErrorToast({ message: "Invalid email", description: errorMessage });
      }
    },
    [router, submitEmail],
  );

  const handleValidateAndAdd = useCallback(() => {
    handleSubmitEmail(email);
  }, [email, handleSubmitEmail]);

  const handleSelectSuggestion = useCallback(
    (suggestion: EmailSuggestion) => {
      handleSubmitEmail(suggestion.email);
    },
    [handleSubmitEmail],
  );

  const handleChangeEmail = (text: string) => {
    setEmail(text);
    setError("");
    setShowSuggestions(true);
  };

  const headerRight = useMemo(
    () => (
      <Pressable
        onPress={handleValidateAndAdd}
        disabled={!email.trim() || isSubmitting}
        hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
        style={{ paddingHorizontal: 16, paddingVertical: 4 }}
      >
        {isSubmitting ?
          <Text textStyle="md/semibold" color="textMuted">
            Adding...
          </Text>
        : <Text textStyle="md/semibold" color={email.trim() ? "navy" : "textMuted"}>
            Add
          </Text>
        }
      </Pressable>
    ),
    [email, handleValidateAndAdd, isSubmitting],
  );

  useLayoutEffect(() => {
    navigation.setOptions({
      title: "Add Email Contact",
      headerBackButtonDisplayMode: "minimal",
      headerRight: () => headerRight,
      headerLeft,
    });
  }, [navigation, headerRight, headerLeft]);

  return (
    <ScreenContainer edges={[]}>
      <ScrollView style={{ flex: 1 }} contentContainerStyle={{ flexGrow: 1, paddingBottom: 20 }}>
        <View px="5xl" py="4xl">
          <View mb="4xl">
            <FormInput
              value={email}
              onChangeText={handleChangeEmail}
              placeholder="email@example.com"
              keyboardType="email-address"
              autoCapitalize="none"
              returnKeyType="done"
              onSubmitEditing={handleValidateAndAdd}
              error={error}
              iconRight={<Icon name="mail" size={20} color={theme.colors.icon} />}
            />
          </View>

          <FeatureGate flag={FEATURE_FLAGS.EMAIL_CONTACT_SUGGESTIONS}>
            {showSuggestions && suggestions.length > 0 && (
              <View mb="4xl">
                <View bg="surface" borderWidth={1} borderColor="border" p="4xl" rounded={8}>
                  <Text textStyle="sm/medium" mb="3xl">
                    Suggestions
                  </Text>
                  {suggestions.map((item, index) => (
                    <PressableScale
                      key={`${item.email}-${index}`}
                      onPress={() => handleSelectSuggestion(item)}
                      style={{
                        paddingVertical: theme.spacing["3xl"],
                        borderBottomWidth: index < suggestions.length - 1 ? 1 : 0,
                        borderBottomColor: theme.colors.border,
                      }}
                    >
                      <Text textStyle="md/regular">{item.email}</Text>
                      {item.name && (
                        <Text textStyle="sm/regular" color="textMuted" mt="xs">
                          {item.name}
                        </Text>
                      )}
                    </PressableScale>
                  ))}
                </View>
              </View>
            )}
          </FeatureGate>
        </View>
      </ScrollView>
    </ScreenContainer>
  );
}
